import { Link } from "react-router";
import {
  Shield,
  FileText,
  AlertTriangle,
  DollarSign,
  ClipboardList,
  Monitor,
  Server,
  MessageSquare,
  Building2,
  FlaskConical,
  Megaphone,
  Truck,
  Scale,
  HardHat,
  Home as HomeIcon,
  ShoppingBag,
  Cpu,
  Phone,
  ChevronRight,
  Play,
  Lock,
  Eye,
  Activity,
  Search,
  ShieldAlert,
  Database,
  Cloud,
  Mic,
  Volume2,
  GraduationCap,
  Users,
  FileCheck,
  BookOpen,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { JsonLd } from "../components/JsonLd";
import { SESSION_AUDIO_MODES, SESSION_AUDIO_PLAN_MATRIX } from "../data/audioCaptureContent";

const SITE_ORIGIN = "https://www.deskreplay.com";

const softwareAppSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "DeskReplay",
  alternateName: ["Desk Replay", "desk replay"],
  applicationCategory: "BusinessApplication",
  operatingSystem: "Windows",
  description:
    "Enterprise screen recording software built for compliance, disputes, forensic audits, and workplace investigations. Captures policy-triggered desktop sessions, indexes content with AI, and exports tamper-proof chain-of-custody evidence.",
  url: SITE_ORIGIN,
  screenshot: `${SITE_ORIGIN}/preview.jpg`,
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "USD",
    offerCount: 3,
    offers: [
      { "@type": "Offer", name: "Pilot", price: "0", priceCurrency: "USD" },
      { "@type": "Offer", name: "Pro", url: `${SITE_ORIGIN}/pricing` },
      { "@type": "Offer", name: "Enterprise", url: `${SITE_ORIGIN}/pricing` },
    ],
  },
  publisher: { "@type": "Organization", name: "DeskReplay", url: SITE_ORIGIN },
};

const dashboardSecImg = "https://images.unsplash.com/photo-1720962158937-7ea890052166?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwc2VjdXJpdHklMjBkYXNoYm9hcmQlMjBkYXJrJTIwdGVjaHxlbnwxfHx8fDE3Nzc5MTUwMTB8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral";

const painCards = [
  {
    icon: AlertTriangle,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    title: "Unreproducible Failures",
    copy: "Dev and QA waste thousands of hours trying to replicate \"works on my machine\" bugs without visual context of the exact moment of failure.",
  },
  {
    icon: DollarSign,
    color: "text-rose-400",
    bg: "bg-rose-500/10",
    border: "border-rose-500/20",
    title: "Expensive Liabilities",
    copy: "Incorrect data entry in Pharma, Ad-Ops overspend, or logistics errors turn into multi-million dollar write-offs when memory fails.",
  },
  {
    icon: ClipboardList,
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
    title: "Audit-Readiness Gaps",
    copy: "Traditional text logs show what happened but miss the human behavior, context, and intent that regulators and auditors actually need.",
  },
  {
    icon: BookOpen,
    color: "text-teal-400",
    bg: "bg-teal-500/10",
    border: "border-teal-500/20",
    title: "No Training Baseline",
    copy: "Without recordings of how your top performers actually work, you are coaching from memory and hoping it sticks. Excellence becomes impossible to replicate.",
  },
];

const steps = [
  {
    step: "01",
    icon: Monitor,
    color: "bg-teal-600",
    shadow: "shadow-teal-500/30",
    title: "Define Policy Triggers",
    subtitle: "Agent Layer",
    copy: "Runs silently in the background of managed Windows endpoints. Capture triggers based on schedules or specific application activity: record only when the Bloomberg Terminal or CRM is active. Optional session audio on Pro and Enterprise adds meeting and call audio to the same recording.",
  },
  {
    step: "02",
    icon: Server,
    color: "bg-emerald-600",
    shadow: "shadow-emerald-500/30",
    title: "Self-Hosted Analysis",
    subtitle: "Server Layer",
    copy: "Stream sessions to your own private infrastructure. AI automatically transcribes all on-screen text and actions, building a searchable archive on your own servers.",
  },
  {
    step: "03",
    icon: MessageSquare,
    color: "bg-teal-500",
    shadow: "shadow-teal-400/30",
    title: "Interact with Evidence",
    subtitle: "Console Layer",
    copy: "Use natural language to search the archive. Do not watch video; ask the AI: \"Where did the user bypass the SOP (Standard Operating Procedure) step?\" and get a timestamped visual answer.",
  },
];

const verticals = [
  { icon: Building2, label: "Banking & Fintech", color: "text-teal-400" },
  { icon: FlaskConical, label: "Pharma & CRO", color: "text-emerald-400" },
  { icon: Shield, label: "Insurance", color: "text-teal-300" },
  { icon: Phone, label: "Telecommunications", color: "text-emerald-300" },
  { icon: Building2, label: "Government & Public", color: "text-teal-400" },
  { icon: Scale, label: "Legal & Professional", color: "text-emerald-400" },
  { icon: Cpu, label: "SaaS & Technology", color: "text-teal-300" },
  { icon: ShoppingBag, label: "E-commerce & Retail", color: "text-emerald-300" },
  { icon: Megaphone, label: "Ad-Ops & Agencies", color: "text-teal-400" },
  { icon: Truck, label: "Logistics & Supply", color: "text-emerald-400" },
  { icon: HardHat, label: "Architecture & Eng.", color: "text-teal-300" },
  { icon: HomeIcon, label: "Real Estate Title", color: "text-emerald-300" },
];

const SOC2_TSC_DISCLAIMER =
  "DeskReplay maps to Trust Services Criteria. We do not claim SOC 2 Type II certification.";

const trustBadges: { label: string; soc2Footnote?: boolean }[] = [
  { label: "GDPR: right to delete" },
  { label: "HIPAA-oriented controls" },
  { label: "SOC 2 TSC alignment", soc2Footnote: true },
  { label: "ISO 27001 control mapping" },
  { label: "DPDP-oriented controls" },
  { label: "FINRA / MiFID II audit readiness" },
  { label: "Okta & Entra ID SSO" },
  { label: "SIEM-compatible" },
  { label: "Forensic chain of custody" },
  { label: "SHA-256 tamper-evident" },
  { label: "Enterprise: your own servers" },
  { label: "Google Drive archive (Pro+)" },
  { label: "Session audio (Pro+)" },
  { label: "Bring-your-own storage (Enterprise)" },
  { label: "Zero-trust ready" },
];

export function Home() {
  return (
    <>
      <Seo
        title="DeskReplay - Screen Recording Built for Compliance"
        description="DeskReplay is enterprise screen recording software built for compliance, disputes, audits, and investigations. Sometimes searched as desk replay. Capture what happened on managed desktops, search with AI, and export tamper-proof chain-of-custody evidence."
        canonicalPath="/"
        keywords="DeskReplay, desk replay, screen recording software, enterprise screen capture, audit trail software, compliance recording, insider threat detection"
      />
      <JsonLd schema={softwareAppSchema} id="software-app" />
      <div>
      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-screen flex items-start overflow-hidden pt-16 lg:pt-20">
        {/* Background Layers */}
        <div className="absolute inset-0 bg-slate-950" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(13, 148, 136, 0.18) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 85% 60%, rgba(5, 150, 105, 0.10) 0%, transparent 55%)",
          }}
        />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.009]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148, 163, 184, 1) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 1) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24 lg:pt-12 lg:pb-32 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              {/* Eyebrow badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-teal-500/25 bg-teal-500/8 mb-8">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                <span className="text-teal-300 text-sm" style={{ fontWeight: 500 }}>
                  Enterprise-ready · Desktop session recording
                </span>
              </div>

              <h1
                className="text-slate-100 mb-4 leading-[1.1]"
                style={{ fontSize: "clamp(2.4rem, 5vw, 3.6rem)", fontWeight: 800, letterSpacing: "-0.03em" }}
              >
                The{" "}
                <span style={{ color: "#008080" }}>
                  system of record
                </span>{" "}
                for what happened on screen.
              </h1>
              <p className="text-teal-300 text-base font-medium mb-5 max-w-xl leading-relaxed">
                Monitor every endpoint. Secure every workflow. Coach every team member with recordings of your best performers.
              </p>
              <p className="text-on-dark-muted text-lg leading-relaxed mb-10 max-w-xl">
                When a single click can cost millions, memory is not evidence. DeskReplay captures what every managed Windows endpoint displays, stores it as forensic-grade video evidence on your own servers, and makes it searchable with AI. Optional meeting and call audio on paid plans.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/request-demo"
                  className="flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-8 py-4 rounded-xl transition-all duration-200 hover:shadow-xl hover:shadow-teal-500/25"
                  style={{ fontWeight: 600, fontSize: "0.95rem" }}
                >
                  <Shield size={18} />
                  Schedule Demo
                </Link>
                <Link
                  to="/platform/security"
                  className="flex items-center justify-center gap-2 border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 px-8 py-4 rounded-xl transition-all duration-200"
                  style={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  <FileText size={18} />
                  How it works for security teams
                </Link>
              </div>

              {/* Social proof */}
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
                <div className="flex items-center gap-2">
                  <Lock size={13} className="text-teal-400" />
                  <span className="text-slate-300 text-sm">Storage on your own servers</span>
                </div>
                <div className="w-px h-4 bg-slate-600 hidden sm:block" />
                <div className="flex items-center gap-2">
                  <Eye size={13} className="text-teal-400" />
                  <span className="text-slate-300 text-sm">Non-Invasive by Design</span>
                </div>
                <div className="w-px h-4 bg-slate-600 hidden sm:block" />
                <div className="flex items-center gap-2">
                  <Activity size={13} className="text-teal-400" />
                  <span className="text-slate-300 text-sm">AI-searchable session archive</span>
                </div>
              </div>
            </div>

            {/* Right: Dashboard Ghost UI (theme-dark: mockup stays a dark console inside .theme-light page) */}
            <div className="relative theme-dark">
              <div
                className="rounded-2xl border border-teal-500/15 overflow-hidden shadow-2xl"
                style={{
                  background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)",
                  boxShadow: "0 0 80px rgba(13, 148, 136, 0.15), 0 30px 70px rgba(7, 26, 33, 0.18)",
                }}
              >
                {/* Window chrome */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-teal-500/10 bg-slate-900/60">
                  <div className="w-3 h-3 rounded-full bg-rose-500/70" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/70" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/70" />
                  <div className="ml-4 flex-1 bg-slate-800/60 rounded px-3 py-1 text-on-dark-muted text-xs">
                    DeskReplay Console: Forensic Archive
                  </div>
                  <div className="w-2 h-2 rounded-full bg-[#008080] animate-pulse" />
                </div>

                {/* Dashboard content */}
                <div className="grid grid-cols-5 h-80">
                  {/* Left: video stream */}
                  <div className="col-span-3 p-3 border-r border-slate-800/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                      <span className="text-rose-400 text-xs" style={{ fontWeight: 500 }}>
                        LIVE CAPTURE
                      </span>
                      <span className="text-on-dark-muted text-xs ml-auto">EP-0047 · Trading Workstation</span>
                    </div>
                    <div
                      className="relative rounded-lg overflow-hidden h-52"
                      style={{ background: "var(--color-slate-950)" }}
                    >
                      <img
                        src={dashboardSecImg}
                        alt="Desktop stream"
                        className="w-full h-full object-cover opacity-50"
                      />
                      <div
                        className="absolute inset-0 pointer-events-none"
                        aria-hidden
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(2, 6, 23, 0.2) 0%, rgba(2, 6, 23, 0.55) 50%, rgba(2, 6, 23, 0.85) 100%)",
                        }}
                      />
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#008080]/20 border border-[#008080]/30 backdrop-blur-sm cursor-pointer hover:bg-[#008080]/30 transition-colors pointer-events-auto"
                        >
                          <Play size={16} className="text-teal-300 ml-0.5" />
                        </div>
                        <span className="text-on-media text-xs">Live stream active</span>
                      </div>
                      {/* Timestamp overlay */}
                      <div className="absolute bottom-2 right-2 badge-on-media">14:32:17 UTC</div>
                    </div>
                    <div className="mt-2 flex gap-3">
                      <div className="flex-1 bg-slate-800/50 rounded p-2">
                        <div className="text-on-dark-teal-accent text-xs mb-1" style={{ fontWeight: 500 }}>Recording</div>
                        <div className="text-slate-300 text-xs">Bloomberg Terminal</div>
                      </div>
                      <div className="flex-1 bg-slate-800/50 rounded p-2">
                        <div className="text-on-dark-teal-accent text-xs mb-1" style={{ fontWeight: 500 }}>Duration</div>
                        <div className="text-slate-300 text-xs">4h 12m 30s</div>
                      </div>
                    </div>
                  </div>

                  {/* Right: AI Chat */}
                  <div className="col-span-2 flex flex-col p-3">
                    <div className="flex items-center gap-1.5 mb-3">
                      <div className="w-5 h-5 rounded bg-teal-600/30 flex items-center justify-center">
                        <MessageSquare size={10} className="text-teal-400" />
                      </div>
                      <span className="text-teal-400 text-xs" style={{ fontWeight: 600 }}>
                        AI Forensic Query
                      </span>
                    </div>
                    <div className="flex-1 space-y-3 overflow-hidden">
                      <div className="bg-slate-800/60 rounded-lg p-2.5 border border-slate-700/50">
                        <p className="text-on-dark-muted text-xs leading-relaxed">
                          "Find the exact moment the wealth management wire instruction was verified."
                        </p>
                      </div>
                      <div className="bg-[#003333]/25 rounded-lg p-2.5 border border-[#008080]/20">
                        <div className="flex items-center gap-1 mb-1">
                          <div className="w-3 h-3 rounded-full bg-[#008080]/60" />
                          <span className="text-on-dark-teal-accent text-xs" style={{ fontWeight: 500 }}>
                            Match Found
                          </span>
                        </div>
                        <p className="text-slate-300 text-xs">
                          Timestamp <span className="text-teal-400">14:04:33 UTC</span>. User confirmed $2.4M wire.
                          SOP step verified.
                        </p>
                      </div>
                      <div className="bg-slate-800/40 rounded-lg p-2.5">
                        <p className="text-on-dark-muted text-xs">
                          "Show all screen captures where the approval dialog appeared..."
                        </p>
                        <div className="flex gap-1 mt-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#008080] animate-bounce" style={{ animationDelay: "0ms" }} />
                          <div className="w-1.5 h-1.5 rounded-full bg-[#008080] animate-bounce" style={{ animationDelay: "150ms" }} />
                          <div className="w-1.5 h-1.5 rounded-full bg-[#008080] animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating stat cards */}
              <div
                className="absolute -bottom-6 -left-6 bg-slate-900 border border-[#008080]/20 rounded-xl px-4 py-3 shadow-xl shadow-slate-900/10"
                style={{ backdropFilter: "blur(8px)" }}
              >
                <div className="text-on-dark-teal-accent text-xs mb-1" style={{ fontWeight: 500 }}>
                  Incident Resolved
                </div>
                <div className="text-slate-100 text-sm" style={{ fontWeight: 700 }}>
                  $2.4M liability avoided
                </div>
              </div>
              <div
                className="absolute -top-4 -right-4 bg-slate-900 border border-[#008080]/20 rounded-xl px-4 py-3 shadow-xl shadow-slate-900/10"
                style={{ backdropFilter: "blur(8px)" }}
              >
                <div className="text-on-dark-teal-accent text-xs mb-1" style={{ fontWeight: 500 }}>
                  Chain of Custody
                </div>
                <div className="text-slate-100 text-sm" style={{ fontWeight: 700 }}>
                  Tamper-Proof ✓
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div
          className="absolute bottom-0 left-0 right-0 h-32"
          style={{ background: "linear-gradient(to bottom, transparent, var(--color-slate-950))" }}
        />
      </section>

      {/* ===== COMPLIANCE TRUST STRIP ===== */}
      <section className="py-10 border-y border-slate-800/60 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 text-xs uppercase tracking-widest mb-5" style={{ fontWeight: 600 }}>
            Trusted compliance frameworks &amp; integrations
          </p>
          <div className="flex flex-wrap justify-center items-center gap-2.5">
            {trustBadges.map((badge) => (
              <span
                key={badge.label}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-700/60 bg-slate-800/50 text-slate-300 text-xs hover:border-teal-500/40 hover:text-slate-100 transition-colors"
                style={{ fontWeight: 500 }}
                title={badge.soc2Footnote ? SOC2_TSC_DISCLAIMER : undefined}
              >
                <Shield size={10} className="text-teal-400 shrink-0" />
                {badge.label}
                {badge.soc2Footnote ? (
                  <span
                    className="text-teal-400/90 font-semibold leading-none cursor-help"
                    title={SOC2_TSC_DISCLAIMER}
                    aria-hidden
                  >
                    *
                  </span>
                ) : null}
              </span>
            ))}
          </div>
          <p
            id="home-soc2-tsc-footnote"
            className="text-center text-slate-500 text-[11px] mt-4 max-w-xl mx-auto leading-relaxed px-2"
          >
            <span className="text-teal-500/90 font-semibold" aria-hidden>
              *
            </span>{" "}
            {SOC2_TSC_DISCLAIMER}
          </p>
        </div>
      </section>

      {/* ===== THREE PILLARS ===== */}
      <section className="py-20 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-3">What DeskReplay does</p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.15" }}
            >
              One platform. Three forces that protect and grow your business.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Monitor */}
            <div
              className="rounded-2xl border border-teal-500/20 p-8 flex flex-col"
              style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--color-teal-500) 9%, var(--color-slate-900)) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center mb-5">
                <Monitor size={22} className="text-teal-400" />
              </div>
              <h3 className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-2">Monitor</h3>
              <p className="text-slate-100 font-bold text-lg mb-3 leading-snug">
                See every endpoint in real time and on demand.
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Live view, policy-scheduled recording, app-trigger capture, spot-checks, and on-demand recording. Know exactly what is happening on every managed Windows endpoint, at any moment.
              </p>
            </div>

            {/* Secure */}
            <div
              className="rounded-2xl border border-emerald-500/20 p-8 flex flex-col"
              style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--color-emerald-500) 9%, var(--color-slate-900)) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-5">
                <Shield size={22} className="text-emerald-400" />
              </div>
              <h3 className="text-emerald-400 text-xs uppercase tracking-widest font-semibold mb-2">Secure</h3>
              <p className="text-slate-100 font-bold text-lg mb-3 leading-snug">
                Detect threats and enforce policy before they become incidents.
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Automated explicit content and policy alerts, insider threat detection, visual concept scanning, word-match rules, and incident response evidence. Turn recordings into an active security layer.
              </p>
            </div>

            {/* Coach */}
            <div
              className="rounded-2xl border border-teal-500/20 p-8 flex flex-col"
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center mb-5">
                <GraduationCap size={22} className="text-teal-400" />
              </div>
              <h3 className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-2">Coach</h3>
              <p className="text-slate-100 font-bold text-lg mb-3 leading-snug">
                Share recordings of your best performers as training material.
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Use AI to find exemplary sessions, navigate to the exact teaching moment, and share it with your team. Turn your top performers into a living training library without any extra software.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SESSION AUDIO ===== */}
      <section className="py-16 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-emerald-400 text-xs uppercase tracking-widest font-semibold mb-3">Session audio</p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.85rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
            >
              Hear the same moment you see on screen
            </h2>
            <p className="text-on-dark-muted text-sm max-w-2xl mx-auto mt-4 leading-relaxed">
              Two optional modes, policy-scoped: record what plays through the speakers (Pro and above) or add microphone capture (Enterprise). Essential is screen-only.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-6">
              <Volume2 className="text-emerald-400 mb-3" size={24} />
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300 mb-2">
                {SESSION_AUDIO_MODES.system.title} · Pro+
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed">{SESSION_AUDIO_MODES.system.description}</p>
            </div>
            <div className="rounded-xl border border-teal-500/20 bg-teal-500/5 p-6">
              <Mic className="text-teal-400 mb-3" size={24} />
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-300 mb-2">
                {SESSION_AUDIO_MODES.mic.title} · Enterprise
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed">{SESSION_AUDIO_MODES.mic.description}</p>
            </div>
          </div>
          <p className="text-center mt-6 text-on-dark-muted text-xs">
            Essential: {SESSION_AUDIO_PLAN_MATRIX.essential.label}
          </p>
        </div>
      </section>

      {/* ===== STORAGE & ARCHIVE ===== */}
      <section className="py-16 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-3">Recording storage</p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.35rem, 2.5vw, 1.85rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
            >
              Keep recent recordings on DeskReplay, archive long-term evidence to Google Drive or your own storage
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="rounded-xl border border-teal-500/20 bg-teal-500/5 p-6">
              <Cloud className="text-teal-400 mb-3" size={24} />
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-300 mb-2">Pro and Enterprise</p>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Archive any recording to Google Workspace Drive before short-term storage expires. Connect your Google login, and the archive stays searchable in DeskReplay.
              </p>
            </div>
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-6">
              <Database className="text-emerald-400 mb-3" size={24} />
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300 mb-2">Enterprise</p>
              <p className="text-on-dark-muted text-sm leading-relaxed">
                Use your own Cloudflare R2 or AWS S3 storage account as primary storage. Self-hosted deployments can use disks on your own servers with the same Drive archive workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== AI CALLOUT ===== */}
      <section className="py-16 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-teal-500/20 bg-gradient-to-br from-teal-500/8 via-slate-900/60 to-slate-950 p-8 md:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-teal-400/30 bg-teal-400/10 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
              <span className="text-teal-300 text-xs font-semibold uppercase tracking-wider">New - AI-Powered</span>
            </div>
            <h2
              className="text-slate-100 mb-3"
              style={{ fontSize: "clamp(1.4rem, 2.5vw, 2rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.2" }}
            >
              Search your recordings like you search the web.
            </h2>
            <p className="text-on-dark-muted text-base leading-relaxed max-w-2xl">
              Chat with Your Recordings (Pro) lets you ask plain-English questions and get timestamped
              answers in seconds. Basic screen-text search (OCR) finds any text that appeared on screen across your archive.
              Enterprise adds visual concept and image search plus policy alerts.
            </p>
          </div>
        </div>
      </section>

      {/* ===== GHOST COACHING CALLOUT ===== */}
      <section className="py-16 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl border border-teal-500/20 overflow-hidden"
            style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
          >
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="p-10 lg:p-14">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-teal-400/30 bg-teal-400/10 mb-5">
                  <GraduationCap size={12} className="text-teal-400" />
                  <span className="text-teal-300 text-xs font-semibold uppercase tracking-wider">Ghost Coaching</span>
                </div>
                <h2
                  className="text-slate-100 mb-4"
                  style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.2" }}
                >
                  Your best employees are your best trainers. Now prove it.
                </h2>
                <p className="text-on-dark-muted text-base leading-relaxed mb-6">
                  Great performance is hard to explain in a slide deck. With DeskReplay, managers can pull up any session recording, use AI to jump to the exact teaching moment, and share it directly with their team as a training reference. No extra software. No re-recording. Just the sessions you are already capturing, turned into your most effective coaching tool.
                </p>
                <div className="space-y-3 mb-8">
                  {[
                    "Find exemplary sessions instantly with AI semantic search",
                    "Navigate to the key moment: the exact SOP (Standard Operating Procedure) step, the perfect client interaction",
                    "New hires learn from how your top performers actually work, not how someone remembers it",
                  ].map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0 mt-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                      </div>
                      <p className="text-slate-300 text-sm leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
                <Link
                  to="/solutions/process-coaching"
                  className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-teal-500/25"
                >
                  <GraduationCap size={16} />
                  See training use cases
                </Link>
              </div>
              <div className="border-l border-teal-500/10 p-10 lg:p-14 flex flex-col justify-center gap-6">
                {[
                  { icon: Users, title: "Consistent onboarding", body: "Every new hire learns from the same gold-standard sessions, not a different manager's memory each time." },
                  { icon: Search, title: "AI finds the teaching moment", body: "Ask the archive to surface sessions where the SOP was followed perfectly. Seconds, not hours of scrubbing." },
                  { icon: FileCheck, title: "No extra tooling", body: "The same admin console used for compliance gives managers direct access to the coaching archive." },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center shrink-0">
                      <item.icon size={18} className="text-teal-400" />
                    </div>
                    <div>
                      <p className="text-slate-200 font-semibold text-sm mb-1">{item.title}</p>
                      <p className="text-on-dark-muted text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== OPTIONAL MODULES (compact mention) ===== */}
      <section className="py-8 bg-slate-950 border-b border-slate-800/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 rounded-xl border border-emerald-500/15 bg-emerald-500/5 px-6 py-4">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 mt-1 sm:mt-0" />
            <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
              <span className="text-emerald-200 font-semibold">Need call audio or desk identity checks?</span>{" "}
              Session Audio and Webcam Capture are optional add-ons on Pro+ endpoint pools, purchased per seat and layered on your base plan.{" "}
              <Link to="/pricing#session-audio-addon" className="text-teal-400 hover:text-teal-300 font-medium underline-offset-2 hover:underline">
                Session Audio
              </Link>
              {" · "}
              <Link to="/pricing#webcam-capture-addon" className="text-teal-400 hover:text-teal-300 font-medium underline-offset-2 hover:underline">
                Webcam Capture
              </Link>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 rounded-xl border border-amber-500/15 bg-amber-500/5 px-6 py-4">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 mt-1 sm:mt-0" />
            <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
              <span className="text-amber-200 font-semibold">Also running connected security cameras?</span>{" "}
              The optional CCTV+ Plugin indexes NVR streams with the same on-premise AI. Purchased separately per camera.{" "}
              <Link to="/pricing#cctv-plugin" className="text-teal-400 hover:text-teal-300 font-medium underline-offset-2 hover:underline">
                See CCTV+ pricing
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ===== VISUAL AI CAPABILITIES SECTION ===== */}
      <section className="py-20 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-4">
              Pro and Enterprise plans
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.15" }}
            >
              Find anything.{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Flag everything.
              </span>
            </h2>
            <p className="text-on-dark-muted text-base max-w-xl mx-auto mt-4 leading-relaxed">
              Basic OCR on Pro, Advanced OCR and policy enforcement on Enterprise. All processing runs on your server.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Basic OCR card */}
            <div
              className="rounded-2xl border border-teal-500/20 p-8 flex flex-col"
              style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--color-teal-500) 9%, var(--color-slate-900)) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-teal-600/20 border border-teal-500/25 flex items-center justify-center">
                  <Search size={20} className="text-teal-400" />
                </div>
                <span className="px-2.5 py-1 rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-300 text-xs font-semibold uppercase tracking-wider">
                  Pro
                </span>
              </div>
              <h3
                className="text-slate-100 mb-2"
                style={{ fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.01em" }}
              >
                Basic OCR
              </h3>
              <p className="text-teal-400/90 text-sm font-medium mb-3">
                Search any text that appeared on screen.
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Every recording is indexed when saved. Find application names, error messages, customer identifiers,
                and document text across your archive in seconds with timestamped hits.
              </p>
            </div>

            {/* Visual Policy Enforcement card */}
            <div
              className="rounded-2xl border border-emerald-500/20 p-8 flex flex-col"
              style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--color-emerald-500) 9%, var(--color-slate-900)) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-emerald-600/20 border border-emerald-500/25 flex items-center justify-center">
                  <ShieldAlert size={20} className="text-emerald-400" />
                </div>
                <span className="px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
                  Enterprise
                </span>
              </div>
              <h3
                className="text-slate-100 mb-2"
                style={{ fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.01em" }}
              >
                Advanced OCR and Policy
              </h3>
              <p className="text-emerald-400/90 text-sm font-medium mb-3">
                Visual concept search, image match, and automated policy alerts.
              </p>
              <p className="text-on-dark-muted text-sm leading-relaxed flex-1">
                Every recorded segment is automatically scanned by AI with 96.5% accuracy NSFW detection and visual
                concept matching. Define custom policy rules using any text concept or a reference image. Catch explicit
                content, weapons, competitor material, political and religious content, and more. Flagged segments
                land in the Policy Alerts Dashboard for your security or HR team to review.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 rounded-xl border border-slate-700/40 bg-slate-900/40 px-6 py-4">
            <Lock size={14} className="text-teal-400 shrink-0" />
            <p className="text-on-dark-muted text-sm leading-relaxed">
              All AI runs on your own server. No images or video frames are sent to any external API.
            </p>
          </div>
        </div>
      </section>

      {/* ===== PAIN POINTS SECTION ===== */}
      <section className="py-28 bg-slate-950 relative">
        <div
          className="absolute inset-0 opacity-[0.0075]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148, 163, 184, 1) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 1) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-teal-400 text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
              The Operational Blind Spot
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.15" }}
            >
              Stop relying on{" "}
              <span className="text-on-dark-muted">"he-said-she-said"</span> disputes.
            </h2>
            <p className="text-on-dark-muted mt-4 max-w-xl mx-auto text-base leading-relaxed">
              Without forensic-grade evidence, every incident becomes a dispute. Every dispute becomes a liability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {painCards.map((card) => (
              <div
                key={card.title}
                className={`relative rounded-2xl border p-8 group hover:scale-[1.02] transition-all duration-300 ${card.border}`}
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className={`w-12 h-12 rounded-xl ${card.bg} flex items-center justify-center mb-6`}>
                  <card.icon size={22} className={card.color} />
                </div>
                <h3
                  className="text-slate-100 mb-3"
                  style={{ fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.01em" }}
                >
                  {card.title}
                </h3>
                <p className="text-on-dark-muted text-sm leading-relaxed">{card.copy}</p>
                <div
                  className={`absolute top-0 right-0 w-24 h-24 rounded-2xl opacity-5 blur-3xl ${card.bg}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS SECTION ===== */}
      <section
        className="py-28 relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, var(--color-slate-950) 0%, var(--color-slate-900) 50%, var(--color-slate-950) 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            background: "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(13, 148, 136, 0.4) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-emerald-400 text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
              The DeskReplay Shield
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.15" }}
            >
              Turn dead video into{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                searchable operational intelligence.
              </span>
            </h2>
          </div>

          {/* Steps */}
          <div className="relative">
            {/* Connector line */}
            <div
              className="absolute left-8 lg:left-1/2 top-10 bottom-10 w-px hidden md:block"
              style={{
                background:
                  "linear-gradient(180deg, rgba(20, 184, 166, 0.6) 0%, rgba(16, 185, 129, 0.6) 50%, rgba(20, 184, 166, 0.2) 100%)",
              }}
            />

            <div className="flex flex-col gap-8">
              {steps.map((step, i) => (
                <div
                  key={step.step}
                  className={`relative flex flex-col ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} items-center gap-8`}
                >
                  {/* Content card */}
                  <div className="flex-1 md:max-w-sm">
                    <div
                      className="rounded-2xl border border-teal-500/15 p-8 hover:border-teal-500/30 transition-all duration-300"
                      style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-teal-400 text-xs uppercase tracking-widest" style={{ fontWeight: 600 }}>
                          Step {step.step}
                        </span>
                        <div className="flex-1 h-px bg-teal-500/10" />
                        <span className="text-lime-300 text-xs" style={{ fontWeight: 500 }}>
                          {step.subtitle}
                        </span>
                      </div>
                      <h3
                        className="text-slate-100 mb-3"
                        style={{ fontSize: "1.3rem", fontWeight: 700, letterSpacing: "-0.01em" }}
                      >
                        {step.title}
                      </h3>
                      <p className="text-on-dark-muted text-sm leading-relaxed">{step.copy}</p>
                    </div>
                  </div>

                  {/* Center icon */}
                  <div className="relative z-10 shrink-0">
                    <div
                      className={`w-16 h-16 rounded-2xl ${step.color} flex items-center justify-center shadow-xl ${step.shadow}`}
                    >
                      <step.icon size={28} className="text-white" />
                    </div>
                    <div
                      className="absolute -inset-2 rounded-2xl opacity-20 blur-lg"
                      style={{
                        background:
                          i === 1
                            ? "rgba(16, 185, 129, 0.6)"
                            : "rgba(20, 184, 166, 0.6)",
                      }}
                    />
                  </div>

                  {/* Placeholder for opposite side */}
                  <div className="flex-1 md:max-w-sm hidden md:block" />
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ===== VERTICALS GRID SECTION ===== */}
      <section className="py-28 bg-slate-950 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-teal-400 text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
              Vertical Coverage
            </p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.15" }}
            >
              Built for the world's{" "}
              <span className="text-on-dark-muted">highest-risk environments.</span>
            </h2>
            <p className="text-on-dark-muted mt-4 text-base">
              Every sector with compliance obligations, human operators, and costly errors.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {verticals.map((v) => (
              <Link
                key={v.label}
                to="/industries"
                className="group flex flex-col items-center gap-3 p-6 rounded-xl border border-slate-800 hover:border-teal-500/30 bg-slate-900/40 hover:bg-teal-500/5 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-800 group-hover:bg-teal-500/10 flex items-center justify-center transition-colors">
                  <v.icon size={22} className={`${v.color} group-hover:scale-110 transition-transform`} />
                </div>
                <span
                  className="text-slate-300 group-hover:text-slate-100 text-sm text-center transition-colors leading-tight"
                  style={{ fontWeight: 500 }}
                >
                  {v.label}
                </span>
              </Link>
            ))}
          </div>

          {/* Stats bar */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "150K+", label: "Desktop Sessions Indexed", color: "text-teal-400" },
              { value: "10+", label: "Regulated Industries Served", color: "text-emerald-400" },
              { value: "<2s", label: "Semantic Search Response", color: "text-teal-400" },
              { value: "0 bytes", label: "Third-party AI egress (self-hosted)", color: "text-emerald-400" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="text-center p-6 rounded-xl border border-slate-800 bg-slate-900/40"
              >
                <div
                  className={`${stat.color} mb-2`}
                  style={{ fontSize: "2rem", fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  {stat.value}
                </div>
                <div className="text-on-dark-muted text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUST SECTION ===== */}
      <section className="py-20 bg-slate-950 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-3">Why teams trust DeskReplay</p>
            <h2
              className="text-slate-100 max-w-2xl mx-auto"
              style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.2" }}
            >
              Your data stays where you put it. Always.
            </h2>
            <p className="text-on-dark-muted text-base mt-3 max-w-xl mx-auto leading-relaxed">
              Enterprise security is not just about what you capture. It is about who controls the evidence.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Lock,
                color: "text-teal-400",
                bg: "bg-teal-500/10",
                border: "border-teal-500/20",
                title: "Runs On Your Own Servers",
                body: "Enterprise deployments run entirely on your own infrastructure. No footage ever leaves your network.",
              },
              {
                icon: Shield,
                color: "text-emerald-400",
                bg: "bg-emerald-500/10",
                border: "border-emerald-500/20",
                title: "Your AI Stays On Your Servers",
                body: "All screen-text search (OCR), semantic search, and policy AI runs on your own server. No footage is sent to any outside service.",
              },
              {
                icon: Database,
                color: "text-teal-400",
                bg: "bg-teal-500/10",
                border: "border-teal-500/20",
                title: "Digitally Fingerprinted",
                body: "Every recording is fingerprinted when captured. Evidence packages carry a verifiable evidence record that shows any tampering.",
              },
              {
                icon: FileCheck,
                color: "text-emerald-400",
                bg: "bg-emerald-500/10",
                border: "border-emerald-500/20",
                title: "Audit Log for Every Action",
                body: "Every admin access, deletion, and export is permanently logged so investigators can prove who touched what.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className={`rounded-2xl border ${item.border} p-7 flex flex-col`}
                style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
              >
                <div className={`w-11 h-11 rounded-xl ${item.bg} flex items-center justify-center mb-5`}>
                  <item.icon size={20} className={item.color} />
                </div>
                <h3 className="text-slate-100 font-bold text-base mb-2">{item.title}</h3>
                <p className="text-on-dark-muted text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF / COMPLIANCE STRIP ===== */}
      <section
        className="py-20 border-t border-slate-800/50"
        style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            <div>
              <p className="text-on-dark-muted text-sm uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
                Compliance & Governance
              </p>
              <h3
                className="text-slate-100"
                style={{ fontSize: "1.8rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: "1.2" }}
              >
                Help your program meet its obligations.
                <br />
                <span className="text-teal-400">Forensic-ready evidence, not a certification sticker.</span>
              </h3>
              <p className="text-on-dark-muted text-sm max-w-md mt-4 leading-relaxed">
                DeskReplay does not issue GDPR or HIPAA certificates. We ship controls so you can support the right to permanently delete personal data, align with SOC 2 security requirements, and retain evidence your counsel can use. Formal certifications depend on how you deploy it and which auditor you use.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              {["GDPR-oriented", "HIPAA-oriented", "DPDP-oriented", "SOC 2 TSC mapping", "ISO 27001 mapping"].map((badge) => (
                <div
                  key={badge}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl border border-teal-500/20 bg-teal-500/5"
                >
                  <Shield size={14} className="text-teal-400" />
                  <span className="text-teal-300 text-sm" style={{ fontWeight: 600 }}>
                    {badge}
                  </span>
                </div>
              ))}
            </div>
            <Link
              to="/platform"
              className="shrink-0 flex items-center gap-2 border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 px-6 py-3 rounded-xl transition-all duration-200"
              style={{ fontWeight: 500, fontSize: "0.9rem" }}
            >
              View Trust Architecture
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}